import type { ReactNode } from 'react';
import { useTenant } from '@/hooks/useTenant';
import { TenantLoading } from '@/routes/TenantLoading';
import { TenantNotFound } from '@/routes/TenantNotFound';
import { TenantError } from '@/routes/TenantError';

interface TenantGateProps {
  children: ReactNode;
}

/**
 * Gate component that ensures tenant is loaded before rendering children.
 * Shows loading, error, or not found states as appropriate.
 */
export function TenantGate({ children }: TenantGateProps) {
  const { tenant, slug, isLoading, error, retry } = useTenant();

  if (isLoading) {
    return <TenantLoading />;
  }

  if (error) {
    if (error.type === 'not_found' || error.type === 'invalid_subdomain') {
      return <TenantNotFound slug={slug || undefined} />;
    }
    return (
      <TenantError type={error.type} message={error.message} onRetry={retry} />
    );
  }

  if (!tenant) {
    return <TenantNotFound />;
  }

  return <>{children}</>;
}
